/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';

const Accordion = styled(MuiAccordion)({
  backgroundColor: 'transparent',
  border: '0',
  boxShadow: 'none',
  '&:before': {
    backgroundColor: 'transparent',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
});

const AccordionTitle = styled(Typography)({
  fontSize: '13px',
  color: '#767676',
});

const AccordionSummary = styled(MuiAccordionSummary)({
  paddingLeft: '24px',
  maxHeight: 48,
  '&.Mui-expanded': {
    minHeight: 48,
  },
  ':hover': {
    background: '#fff',
    color: '#000',
  },
});

const AccordionDetails = styled(MuiAccordionDetails)({
  padding: 0,
  flexDirection: 'column',
});

interface Props extends AccordionProps {
  label?: string;
  sectionKey: string;
}

/**
 * The CustomAccordion extends the MUIAccordion and requires a sectionKey. The open
 * and closed states are managed internally, but if you need to react to a state
 * change the onChange method is available.
 *
 * More information available in the [Accordion docs](https://mui.com/api/accordion/)
 */
export default function CustomAccordion({ ...props }: Props) {
  const [isOpen, setIsOpen] = React.useState(props.defaultExpanded ?? props.sectionKey);
  const sectionKeyMemo = React.useMemo(() => props.sectionKey, [props.sectionKey]);

  React.useEffect(() => {
    setIsOpen(props.sectionKey);
  }, [props.sectionKey]);

  const handleChange = (key: string) => (
    event: React.SyntheticEvent<Element, Event>,
    expanded: boolean,
  ) => {
    setIsOpen(expanded ? key : '');

    if (props.onChange) {
      props.onChange(event, expanded);
    }
  };

  return (
    <Accordion
      expanded={!!isOpen && isOpen === sectionKeyMemo}
      onChange={handleChange(sectionKeyMemo)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        data-testid="accordion-header"
      >
        <AccordionTitle>{props.label}</AccordionTitle>
      </AccordionSummary>
      <AccordionDetails data-testid="accordion-content">
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
}
